import { useApiQuery } from './useApiQuery';
import { useApiMutation } from './useApiMutation';
import { teamsApi, teamJoinRequestsApi } from '../lib/api';
import type { TeamBrandingPreviewParams } from '../lib/api/teams';
import { log } from '@/lib/logger';

log.info('[useTeams] Module loaded');

export function useTeams() {
  log.info('[useTeams] useTeams called');
  return useApiQuery({
    queryKey: ['teams'],
    queryFn: (token) => teamsApi.list(token),
  });
}

export function useTeam(id: string) {
  log.info('[useTeams] useTeam called with id:', { id });
  return useApiQuery({
    queryKey: ['teams', id],
    queryFn: (token) => teamsApi.get(token, id),
    enabled: !!id,
  });
}

// ── Members ──────────────────────────────────────────────────
export function useTeamMembers(teamId: string | undefined) {
  log.info('[useTeams] useTeamMembers called with teamId:', { teamId });
  return useApiQuery({
    queryKey: ['teams', teamId, 'members'],
    queryFn: (token) => teamsApi.listMembers(token, teamId!),
    enabled: !!teamId,
  });
}

export function useRemoveTeamMember(teamId: string | undefined) {
  log.info('[useTeams] useRemoveTeamMember called');
  return useApiMutation<{ removed: true }, { userId: string }>({
    mutationFn: (token, { userId }) => teamsApi.removeMember(token, teamId!, userId),
    invalidateKeys: teamId ? [['teams', teamId, 'members']] : [],
  });
}

// Self-leave: the caller removes their own membership. On success we invalidate
// the app session (prefix-matches ['app-session', userId]) so the active team
// re-resolves in place to the caller's default team, plus the members list.
export function useLeaveTeam(teamId: string | undefined) {
  log.info('[useTeams] useLeaveTeam called');
  return useApiMutation<{ left: true }, void>({
    mutationFn: (token) => teamsApi.leaveTeam(token, teamId!),
    invalidateKeys: teamId ? [['app-session'], ['teams', teamId, 'members']] : [['app-session']],
  });
}

// ── Invitations ──────────────────────────────────────────────
export function useTeamInvitations(teamId: string | undefined) {
  log.info('[useTeams] useTeamInvitations called with teamId:', { teamId });
  return useApiQuery({
    queryKey: ['teams', teamId, 'invitations'],
    queryFn: (token) => teamsApi.listInvitations(token, teamId!),
    enabled: !!teamId,
  });
}

export function useInviteTeamMember(teamId: string | undefined) {
  log.info('[useTeams] useInviteTeamMember called');
  return useApiMutation<
    Awaited<ReturnType<typeof teamsApi.invite>>,
    { email: string; role: 'admin' | 'member' }
  >({
    // Both forms render the route's refusals inline (seat cap 403, already a
    // member 409, email provider 500), as onboarding's handleInvite does, so
    // they log at warn instead of minting a fake $exception.
    mutationFn: (token, data) =>
      teamsApi.invite(token, teamId!, data, { expectedErrorStatuses: [403, 409, 500] }),
    invalidateKeys: teamId ? [['teams', teamId, 'invitations']] : [],
  });
}

export function useRevokeTeamInvitation(teamId: string | undefined) {
  log.info('[useTeams] useRevokeTeamInvitation called');
  return useApiMutation<{ revoked: true }, { invitationId: string }>({
    mutationFn: (token, { invitationId }) =>
      teamsApi.revokeInvitation(token, teamId!, invitationId),
    invalidateKeys: teamId ? [['teams', teamId, 'invitations']] : [],
  });
}

export function useAcceptTeamInvitation() {
  log.info('[useTeams] useAcceptTeamInvitation called');
  return useApiMutation<
    Awaited<ReturnType<typeof teamsApi.acceptInvitation>>,
    { inviteToken: string }
  >({
    mutationFn: (token, { inviteToken }) => teamsApi.acceptInvitation(token, inviteToken),
    invalidateKeys: [['teams']],
  });
}

// ── Branding (spec 2026-09-01) ───────────────────────────────
export function useTeamBranding(teamId: string | undefined) {
  log.info('[useTeams] useTeamBranding called with teamId:', { teamId });
  return useApiQuery({
    queryKey: ['teams', teamId, 'branding'],
    queryFn: (token) => teamsApi.getBranding(token, teamId!),
    enabled: !!teamId,
  });
}

export function useUpdateTeamBranding(teamId: string | undefined) {
  log.info('[useTeams] useUpdateTeamBranding called');
  return useApiMutation<unknown, { accentColor?: string | null; backgroundColor?: string | null; hidePoweredBy?: boolean }>({
    mutationFn: (token, data) => teamsApi.updateBranding(token, teamId!, data),
    invalidateKeys: teamId ? [['teams', teamId, 'branding']] : [],
  });
}

export function useUploadTeamLogo(teamId: string | undefined) {
  log.info('[useTeams] useUploadTeamLogo called');
  return useApiMutation<unknown, { dataUrl: string; filename: string }>({
    mutationFn: (token, { dataUrl, filename }) => teamsApi.uploadBrandingLogo(token, teamId!, dataUrl, filename),
    invalidateKeys: teamId ? [['teams', teamId, 'branding']] : [],
  });
}

/**
 * The live branding preview for the colours being typed (audit 2026-09-03).
 * Keyed on the raw params so an unchanged value never refetches; the caller
 * debounces. Previous data is kept while a new key loads so the frame does not
 * blink white between keystrokes. The stored logo URL is part of the key: the
 * logo mutations invalidate the branding key and not this one, so without it
 * the cached frame outlived an upload (frame 13, 2026-09-03).
 */
export function useBrandingPreview(
  teamId: string | undefined,
  params: TeamBrandingPreviewParams,
  enabled = true,
) {
  return useApiQuery({
    queryKey: ['teams', teamId, 'branding-preview', params.accent, params.background, params.hidePoweredBy, params.logoUrl],
    queryFn: (token) => teamsApi.previewBranding(token, teamId!, params),
    enabled: !!teamId && enabled,
    staleTime: 60_000,
    placeholderPreviousData: true,
  });
}

export function useDeleteTeamLogo(teamId: string | undefined) {
  log.info('[useTeams] useDeleteTeamLogo called');
  return useApiMutation<unknown, void>({
    mutationFn: (token) => teamsApi.deleteBrandingLogo(token, teamId!),
    invalidateKeys: teamId ? [['teams', teamId, 'branding']] : [],
  });
}

// ── Join requests (spec 2026-09-10) ──────────────────────────────────
/**
 * The teams the signed-in user may ask to join (owner-matched by work-email
 * domain). Empty while the server flag is off, which is what makes the wizard
 * render today's invite form in that case. `enabled` lets the page hold the
 * read until the session exists and skip it for the invited slim wizard.
 */
export function useJoinCandidates(options: { enabled: boolean }) {
  log.info('[useTeams] useJoinCandidates called', { enabled: options.enabled });
  return useApiQuery({
    queryKey: ['team-join-requests', 'candidates'],
    queryFn: (token) => teamJoinRequestsApi.candidates(token),
    enabled: options.enabled,
  });
}

/** Pending requests on a team. Pass undefined for a plain member: the route 403s a non-admin. */
export function useTeamJoinRequests(teamId: string | undefined) {
  log.info('[useTeams] useTeamJoinRequests called with teamId:', { teamId });
  return useApiQuery({
    queryKey: ['teams', teamId, 'join-requests'],
    queryFn: (token) => teamJoinRequestsApi.listForTeam(token, teamId!),
    enabled: !!teamId,
  });
}

export function useApproveJoinRequest(teamId: string | undefined) {
  log.info('[useTeams] useApproveJoinRequest called');
  return useApiMutation<Awaited<ReturnType<typeof teamJoinRequestsApi.approve>>, { requestId: string }>({
    mutationFn: (token, data) => teamJoinRequestsApi.approve(token, data.requestId),
    // Approval moves the person from this list into Pending invitations, so both keys refresh.
    invalidateKeys: teamId ? [['teams', teamId, 'join-requests'], ['teams', teamId, 'invitations']] : [],
  });
}

export function useDeclineJoinRequest(teamId: string | undefined) {
  log.info('[useTeams] useDeclineJoinRequest called');
  return useApiMutation<Awaited<ReturnType<typeof teamJoinRequestsApi.decline>>, { requestId: string }>({
    mutationFn: (token, data) => teamJoinRequestsApi.decline(token, data.requestId),
    invalidateKeys: teamId ? [['teams', teamId, 'join-requests']] : [],
  });
}
